import type { MyInvite } from '../../api/invites'
import { isTimeTbd, type MyMatch } from '../../api/matches'
import type { MyTeam, OrgSeason } from '../../api/teams'

/**
 * The "needs you" block: one ranked list of everything asking for the user's
 * attention, across every role they hold.
 *
 * A single account can be a player, a club owner, a league admin and a
 * university admin at once, and each of those produces its own kind of nag. If
 * each role got its own block the page would be a wall of headings, most of
 * them empty. So they compete in ONE list, ranked by urgency, and the user
 * reads from the top.
 *
 * Two rules keep that list honest:
 *  - Three or more of the same kind collapse into one summary row, so five
 *    pending invites cannot crowd out a blocked registration.
 *  - The list is capped, because a dashboard that shows everything ranks
 *    nothing. Anything below the cap is reachable from its own page.
 */

export type NeedsYouTone = 'critical' | 'warning' | 'info' | 'neutral'

export interface NeedsYouItem {
  id: string
  tone: NeedsYouTone
  title: string
  description?: string
  actionLabel?: string
  actionTo?: string
  /** Lower sorts first. Ties break on `id` so the order is stable across renders. */
  rank: number
  /** Items sharing a kind can be collapsed into one summary row. */
  kind: NeedsYouKind
  /**
   * The team or org the item belongs to, so the home page can group items
   * under it and name it once. Absent for the viewer's own account (trust)
   * and for summaries that span several places.
   */
  place?: NeedsYouPlace
}

export interface NeedsYouPlace {
  /** Stable grouping key: the team or org id. */
  key: string
  name: string
  logoUrl?: string
  /** Brand colour, for the rule under the logo. */
  color?: string
  /** What the viewer is to it: "Your team · captain", "League you run". */
  role?: string
  /** For a team: the club it belongs to, so the club's own summary can find it. */
  parentKey?: string
}

export type NeedsYouKind = 'trust' | 'invite' | 'checkin' | 'roster' | 'applications'

/** How many rows the block shows before it stops being scannable. */
export const MAX_VISIBLE = 3

/** Same-kind items collapse into a summary at this count. */
export const AGGREGATE_AT = 3

/**
 * Trust Score needed to register for a season. Mirrors the `Verified` level in
 * api/internal/auth/trust_score.go — levels are Unverified 0, Basic 1,
 * Verified 20, Established 50, Veteran 80.
 */
export const MIN_TRUST_TO_COMPETE = 20

const HOUR_MS = 60 * 60 * 1000

/** An invite this close to lapsing is worth interrupting someone over. */
const INVITE_URGENT_HOURS = 48

/** Check-in opens about here, so this is the last useful moment to say so. */
const CHECKIN_WINDOW_HOURS = 2

export interface NeedsYouInput {
  trustScore: number | null
  invites: MyInvite[]
  /** Upcoming matches, soonest first — the caller's own schedule. */
  matches: MyMatch[]
  /** Teams the caller is on; a roster below the game's team size blocks entry. */
  teams?: MyTeam[]
  /**
   * Whether the caller may manage a given team's roster. A short roster is only
   * "needs YOU" for someone who can fill it; for anyone else it is a fact about
   * their team, which the teams table already states.
   */
  canManageTeam?: (team: MyTeam) => boolean
  /** Live seasons the caller operates; pending registrations wait on them. */
  seasons?: OrgSeason[]
  /** Slug of the league a season belongs to, for linking. */
  seasonHref?: (season: OrgSeason) => string | undefined
  /** The league a season belongs to, for grouping. */
  seasonPlace?: (season: OrgSeason) => NeedsYouPlace | undefined
  /** Injected so the ranking is testable and does not drift with the clock. */
  now?: Date
}

/**
 * Build every candidate row. Exported for tests: the ranking is only meaningful
 * if you can see what it ranked, and the aggregation hides that.
 */
export function buildNeedsYou(input: NeedsYouInput): NeedsYouItem[] {
  const now = input.now ?? new Date()
  const items: NeedsYouItem[] = []
  const teamPlace = (team: MyTeam): NeedsYouPlace => ({
    key: team.id,
    name: team.name,
    logoUrl: team.logo_url || undefined,
    color: team.primary_color || undefined,
    role: team.role === 'captain' ? 'Your team · captain' : 'Your team',
    parentKey: team.organization_id,
  })
  // The viewer's side in a fixture: /matches/mine lists both teams.
  const myTeamIn = (match: MyMatch): MyTeam | undefined =>
    input.teams?.find((t) => match.teams.some((mt) => mt.team_id === t.id))

  // 1. Blocked outright. Nothing else on the page can be acted on until this
  //    is fixed, so it outranks everything including a match starting now.
  if (input.trustScore !== null && input.trustScore < MIN_TRUST_TO_COMPETE) {
    items.push({
      id: 'trust',
      kind: 'trust',
      tone: 'critical',
      rank: 0,
      title: 'Verify your identity to compete',
      description: `Your Trust Score is ${input.trustScore}. Leagues require ${MIN_TRUST_TO_COMPETE} to register.`,
      actionLabel: 'Link accounts',
      actionTo: '/settings/connections',
    })
  }

  // 2. A match about to start. Time-boxed and unrecoverable once missed.
  for (const match of input.matches) {
    const startsIn = new Date(match.starts_at).getTime() - now.getTime()
    if (match.status === 'live') {
      items.push({
        id: `live-${match.id}`,
        kind: 'checkin',
        tone: 'critical',
        rank: 5,
        title: `${match.game_name} match is live now`,
        description: describeMatch(match),
        actionLabel: 'Go to match',
        actionTo: matchPath(match),
        place: placeOf(myTeamIn(match)),
      })
      continue
    }
    // No known time, no countdown: a TBD placeholder inside the window is not
    // a match "in 2h".
    if (isTimeTbd(match)) continue
    if (startsIn > 0 && startsIn <= CHECKIN_WINDOW_HOURS * HOUR_MS) {
      items.push({
        id: `checkin-${match.id}`,
        kind: 'checkin',
        tone: 'info',
        rank: 20,
        title: `${match.game_name} match in ${formatIn(startsIn)}`,
        description: describeMatch(match),
        actionLabel: 'Check in',
        actionTo: matchPath(match),
        place: placeOf(myTeamIn(match)),
      })
    }
  }

  // 3. A roster that cannot field a side. This blocks the team from entering
  //    anything, and only a captain or org admin can fix it — so it outranks an
  //    invite, but ONLY for someone who can act on it.
  //
  //    Production regression: this used to fire for every short roster the
  //    caller was merely on, and its "Invite players" button linked to team
  //    management that the admin gate refuses a plain player — Access Denied.
  //    With no permission check supplied, nothing is raised: an unactionable
  //    nag is worse than none.
  for (const team of input.teams ?? []) {
    const missing = team.team_size - team.member_count
    if (missing <= 0) continue
    if (!input.canManageTeam?.(team)) continue
    items.push({
      id: `roster-${team.id}`,
      kind: 'roster',
      tone: 'critical',
      rank: 8,
      title: `${team.name} is ${missing} player${missing === 1 ? '' : 's'} short`,
      description: team.competing_in
        ? `Entered in ${team.competing_in} with ${team.member_count} of ${team.team_size}.`
        : `${team.member_count} of ${team.team_size} rostered — it cannot enter a season.`,
      actionLabel: 'Invite players',
      actionTo: `/org/${team.organization_slug}/manage/teams`,
      place: teamPlace(team),
    })
  }

  // 4. Applications waiting on an admin. Nobody else can clear these, and a
  //    team sitting unreviewed is a team that may go elsewhere.
  for (const season of input.seasons ?? []) {
    const pending = season.pending_registrations
    if (pending <= 0) continue
    items.push({
      id: `apps-${season.id}`,
      kind: 'applications',
      tone: 'warning',
      rank: 15,
      title: `${pending} team${pending === 1 ? '' : 's'} applied to ${season.name}`,
      description: `${season.registered_teams} entered so far.`,
      actionLabel: 'Review applications',
      actionTo: input.seasonHref?.(season),
      place: input.seasonPlace?.(season),
    })
  }

  // 5. Invites. An expiring one is a deadline; a fresh one is just a to-do, so
  //    they rank apart rather than as one undifferentiated pile.
  for (const invite of input.invites) {
    const expiresIn = new Date(invite.expires_at).getTime() - now.getTime()
    if (expiresIn <= 0) continue // already lapsed — not the user's problem any more
    const urgent = expiresIn <= INVITE_URGENT_HOURS * HOUR_MS
    items.push({
      id: `invite-${invite.id}`,
      kind: 'invite',
      tone: urgent ? 'warning' : 'neutral',
      rank: urgent ? 10 : 50,
      title: `Invite from ${invite.organization.name}`,
      description: urgent
        ? `Expires in ${formatIn(expiresIn)} · joining as ${invite.role}`
        : `Joining as ${invite.role}`,
      actionLabel: 'Review',
      actionTo: `/invites/${invite.token}`,
      place: {
        key: `invite-org-${invite.organization.slug}`,
        name: invite.organization.name,
        logoUrl: invite.organization.logo_url || undefined,
        role: 'Invited you',
      },
    })
  }

  return items.sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id))

  function placeOf(team: MyTeam | undefined): NeedsYouPlace | undefined {
    return team ? teamPlace(team) : undefined
  }
}

/**
 * Collapse over-represented kinds, then cap.
 *
 * Aggregation happens BEFORE the cap on purpose. Capping first would let three
 * invites fill the block and push a critical row off the bottom — the exact
 * failure this is here to prevent.
 */
export function rankNeedsYou(input: NeedsYouInput, options: { limit?: number } = {}): NeedsYouItem[] {
  const all = buildNeedsYou(input)

  const byKind = new Map<NeedsYouKind, NeedsYouItem[]>()
  for (const item of all) {
    const bucket = byKind.get(item.kind)
    if (bucket) bucket.push(item)
    else byKind.set(item.kind, [item])
  }

  const collapsed: NeedsYouItem[] = []
  const seen = new Set<NeedsYouKind>()
  for (const item of all) {
    const bucket = byKind.get(item.kind) ?? []
    if (bucket.length < AGGREGATE_AT) {
      collapsed.push(item)
      continue
    }
    if (seen.has(item.kind)) continue
    seen.add(item.kind)
    // The summary inherits the most urgent member's tone and rank, so
    // collapsing never quietly demotes the group.
    // There is no index page for any of these kinds, so the summary borrows
    // the most urgent member's action. "4 invites waiting -> Review" opens the
    // one that lapses first, which is the one that actually needs deciding.
    collapsed.push({
      ...summarize(item.kind, bucket.length),
      tone: bucket[0].tone,
      rank: bucket[0].rank,
      actionLabel: bucket[0].actionLabel,
      actionTo: bucket[0].actionTo,
      // A summary only belongs to a place when every item in it does.
      place: bucket.every((b) => b.place?.key === bucket[0].place?.key) ? bucket[0].place : undefined,
    })
  }

  return collapsed.sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id)).slice(0, options.limit ?? MAX_VISIBLE)
}

function summarize(kind: NeedsYouKind, count: number): NeedsYouItem {
  switch (kind) {
    case 'invite':
      return {
        id: `invites-${count}`,
        kind,
        tone: 'warning',
        rank: 10,
        title: `${count} invites waiting`,
        description: 'Organizations want you on their roster.',
      }
    case 'roster':
      return {
        id: `rosters-${count}`,
        kind,
        tone: 'critical',
        rank: 8,
        title: `${count} teams are short of a full roster`,
        description: 'They cannot enter a season until they are complete.',
      }
    case 'applications':
      return {
        id: `apps-${count}`,
        kind,
        tone: 'warning',
        rank: 15,
        title: `${count} seasons have applications waiting`,
        description: 'Teams are waiting on your review.',
      }
    case 'checkin':
      return {
        id: `checkins-${count}`,
        kind,
        tone: 'info',
        rank: 20,
        title: `${count} matches starting soon`,
        description: 'Check in before they begin.',
      }
    default:
      return {
        id: `${kind}-${count}`,
        kind,
        tone: 'neutral',
        rank: 50,
        title: `${count} items need you`,
      }
  }
}

/**
 * Match detail lives in the org remote at `schedule/:matchId` — there is no
 * cross-org match route, so a fixture whose host we cannot name has nowhere to
 * link and the caller must omit the action rather than invent a URL.
 */
export function matchPath(match: MyMatch): string | undefined {
  const slug = match.organization?.slug
  return slug ? `/org/${slug}/schedule/${match.id}` : undefined
}

function describeMatch(match: MyMatch): string {
  const opponent = match.teams.map((t) => t.team_name).join(' vs ')
  const host = match.organization?.name
  return [opponent, host].filter(Boolean).join(' · ')
}

/** Coarse on purpose — "in 2 days" is as actionable as an exact duration. */
export function formatIn(ms: number): string {
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${Math.max(minutes, 1)} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const days = Math.round(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'}`
}
